import { Box } from "@mui/material";
import {
  SeedingStrategy,
  SplitVolDict,
  WorklistValues,
} from "features/WorklistTools/shared/interfaces";
import { isStringifiedInteger } from "features/WorklistTools/shared/WorklistHelpers";
import { useAppReduxSelector } from "hooks/reduxHooks";
import _ from "lodash";
import { useEffect, useState } from "react";
import { TextEditor } from "react-data-grid";
import { useDispatch } from "react-redux";
import { Alert, AlertType } from "shared-components/toast";
import {
  buildSplitVolDictionary,
  getSplitEvenlyTransferVol,
  getVolRemaining,
} from "../../ExternalThawToolHelpers";
import {
  ExternalThawToolActions,
  IntermediatePlateInfoState,
} from "../../state";
import { useExternalThawStateProvider } from "../../state/StateProvider";
import { WellMapping } from "../WellMapping";
import { WellMappingTableHeader } from "../WellMappingTableHeader";

export const DestinationWellMapping = () => {
  const dispatch = useDispatch();
  const reduxState = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present
  );

  const worklistValues = reduxState.worklistValues;
  const intPlateInfo = reduxState.intPlateInfo[0] as IntermediatePlateInfoState;
  const [splitVolDict, setSplitVolDict] = useState({} as SplitVolDict);
  const { externalThawInternalState } = useExternalThawStateProvider();

  const methodSettings = externalThawInternalState.methodSettings;
  const getTransferVolColName = () => {
    return methodSettings.seedingStrategy === SeedingStrategy.CellCount
      ? "Cell Count"
      : "Transfer Vol";
  };

  const customSplitVol =
    methodSettings.seedingStrategy === SeedingStrategy.Volumetric &&
    !methodSettings.splitSeedingVolEvenly;

  const columns = [
    { key: "sourceWellId", name: "IntPos", resizable: true },
    ...(customSplitVol
      ? [
          {
            key: "volremaining",
            name: "Vol Remaining",
            cellClass: (row: any) => {
              const srcWellId = row.sourceWellId;
              const volRemaining = getVolRemaining(
                srcWellId,
                splitVolDict,
                intPlateInfo.resuspensionVol,
                methodSettings.numberOfAliquots,
                methodSettings.aliquotVolume
              );
              return volRemaining < 0 ? "negative-cell" : undefined;
            },
            formatter(props: any) {
              const srcWellId = props.row.sourceWellId;

              const volRemaining = getVolRemaining(
                srcWellId,
                splitVolDict,
                intPlateInfo.resuspensionVol,
                methodSettings.numberOfAliquots,
                methodSettings.aliquotVolume
              );
              const tooMuchVolUsed = volRemaining < 0;

              return (
                <span
                  style={{
                    color: tooMuchVolUsed ? "red" : "inherit",
                  }}
                >
                  {volRemaining}
                </span>
              );
            },
            resizable: true,
            editable: false,
          },
        ]
      : []),
    {
      key: "destPlateIndex",
      name: "DestPlate",
      formatter(props: any) {
        return <>{props.row.destPlateIndex + 1}</>;
      },
      resizable: true,
    },
    { key: "destWellId", name: "DestPos", resizable: true },
    {
      key: "transferVol",
      name: getTransferVolColName(),
      resizable: true,
      editor: TextEditor,
      editable: !externalThawInternalState.methodSettings.splitSeedingVolEvenly,
      formatter({ row }: any) {
        const value = row.transferVol;
        const parsed = parseInt(value, 10);
        return <>{!isNaN(parsed) ? parsed.toLocaleString("en-US") : value}</>;
      },
    },
  ];

  const handleApplyCellNumber = (
    option: string | number,
    cellNumber: number | undefined
  ) => {
    if (!cellNumber) {
      return Alert({
        type: AlertType.ERROR,
        message: "Please enter a number to apply",
      });
    }
    switch (option) {
      case "Apply to All":
        dispatch(
          ExternalThawToolActions.UPDATE_ALL_DEST_STAMP_CELL_NUMBER({
            transferVol: cellNumber,
          })
        );
        break;
      case "Apply to Empty":
        dispatch(
          ExternalThawToolActions.UPDATE_EMPTY_DEST_CELL_NUMBERS({
            transferVol: cellNumber,
          })
        );
        break;
      default:
        dispatch(
          ExternalThawToolActions.UPDATE_SPECIFIC_DEST_CELL_NUMBERS({
            transferVol: cellNumber,
            destPlateIndex: option,
          })
        );
        break;
    }
  };

  const setSingleCellNumber = (rows: unknown[]) => {
    const typedRows = rows as WorklistValues[];

    for (const row of typedRows) {
      if (
        row.transferVol !== "" &&
        row.transferVol &&
        !isStringifiedInteger(row.transferVol)
      ) {
        return Alert({
          type: AlertType.ERROR,
          message: "Please enter a valid whole number",
        });
      }
    }
    dispatch(
      ExternalThawToolActions.UPDATE_DEST_CELL_NUMBER({
        rows,
      })
    );
  };

  useEffect(() => {
    if (
      externalThawInternalState.methodSettings.seedingStrategy ===
        SeedingStrategy.CellCount ||
      worklistValues.int1ToDest.length === 0
    ) {
      setSplitVolDict({} as SplitVolDict);
      return;
    }
    setSplitVolDict(buildSplitVolDictionary(worklistValues.int1ToDest));
  }, [
    worklistValues.int1ToDest,
    externalThawInternalState.methodSettings.seedingStrategy,
  ]);

  useEffect(() => {
    if (
      externalThawInternalState.methodSettings.splitSeedingVolEvenly ===
        false ||
      worklistValues.int1ToDest.length === 0 ||
      Object.keys(splitVolDict).length === 0
    ) {
      return;
    }

    const updatedWellMappings = worklistValues.int1ToDest.map((wellMapping) => {
      const splitEvenlyTransferVol = getSplitEvenlyTransferVol(
        intPlateInfo.resuspensionVol,
        methodSettings.numberOfAliquots,
        methodSettings.aliquotVolume,
        wellMapping.sourceWellId,
        splitVolDict
      );
      return {
        ...wellMapping,
        transferVol: splitEvenlyTransferVol.toString(),
      };
    });

    const noChangesToWellMapping = _.isEqual(
      worklistValues.int1ToDest,
      updatedWellMappings
    );

    if (noChangesToWellMapping) {
      return;
    }
    setSingleCellNumber(updatedWellMappings);
  }, [
    splitVolDict,
    methodSettings.splitSeedingVolEvenly,
    intPlateInfo.resuspensionVol,
    methodSettings.numberOfAliquots,
    methodSettings.aliquotVolume,
  ]);
  return (
    <Box>
      <WellMapping
        setSingleCellNumber={setSingleCellNumber}
        columns={columns}
        worklistValues={worklistValues.int1ToDest}
        tableHeader={
          methodSettings.splitSeedingVolEvenly ? null : (
            <WellMappingTableHeader
              handleApplyCellNumber={handleApplyCellNumber}
              destPlates={reduxState.destPlateInfo}
            />
          )
        }
      />
    </Box>
  );
};
